import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Card,
	CardContent,
	Alert,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	List,
	ListItem,
	ListItemText
} from '@mui/material';
import {
	Warning as WarningIcon,
	Info as InfoIcon,
	Security as SecurityIcon,
	Description as DescriptionIcon,
	ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

interface DisclaimerModalProps {
	open: boolean;
	onClose: () => void;
}

const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ open, onClose }) => {
	const metadataAllowlist: string[] = [
		'InstalledPackage',
		'CustomObject',
		'CustomField',
		'FieldSet',
		'RecordType',
		'CustomApplication',
		'CustomTab',
		'FlexiPage',
		'Layout',
		'WebLink',
		'Flow',
		'FlowDefinition',
		'ApexClass',
		'ApexTrigger',
		'CustomMetadata',
		'CustomPermission',
		'MutingPermissionSet',
		'PermissionSet',
		'PermissionSetGroup',
		'Profile'
	];

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
					}
				}
			}}
		>
			<DialogTitle sx={{ textAlign: 'center', pb: 2, pt: 3 }}>
				<Box display="flex" alignItems="center" justifyContent="center" gap={2}>
					<WarningIcon sx={{ fontSize: 32, color: 'warning.main' }} />
					<Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
						Proof of Concept: Limited Access
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent sx={{ px: 4, py: 2 }}>
				<Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
					<Typography variant="body1" fontWeight="medium">
						This application is a <strong>Proof of Concept</strong> and not ready for
						widespread use. It is open to a small set of users to provide intermediate value and gather feedback
						while we explore complex, unfinished areas. It is not intended to be a production-ready application.
					</Typography>
				</Alert>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Card variant="outlined" sx={{ borderRadius: 2 }}>
						<CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<InfoIcon color="primary" />
								<Typography variant="h6" fontWeight="bold">
									Active Development
								</Typography>
							</Box>
							<Typography variant="body2" color="text.secondary">
								Updates are deployed regularly. If you hit an issue, retry after a few
								minutes and report it if it persists.
							</Typography>
						</CardContent>
					</Card>

					<Card variant="outlined" sx={{ borderRadius: 2 }}>
						<CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<SecurityIcon color="success" />
								<Typography variant="h6" fontWeight="bold">
									Sandbox Recommended
								</Typography>
							</Box>
							<Typography variant="body2" color="text.secondary">
								There is no hard sandbox limitation, but please use sandbox or trial orgs
								only at this time.
							</Typography>
						</CardContent>
					</Card>

					<Card variant="outlined" sx={{ borderRadius: 2 }}>
						<CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<DescriptionIcon color="info" />
								<Typography variant="h6" fontWeight="bold">
									Limited Metadata Coverage
								</Typography>
							</Box>
							<Typography variant="body2" color="text.secondary">
								Only a subset of metadata is indexed today. Expand below for the current
								coverage list.
							</Typography>
						</CardContent>
					</Card>
				</Box>

				<Accordion sx={{ mt: 2 }}>
					<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="metadata-coverage-content">
						<Typography variant="subtitle1" fontWeight="bold">
							Metadata Coverage (Current Allowlist)
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<List dense>
							{metadataAllowlist.map((metadataType) => (
								<ListItem key={metadataType} disablePadding>
									<ListItemText primary={metadataType} />
								</ListItem>
							))}
						</List>
					</AccordionDetails>
				</Accordion>

				<Divider sx={{ my: 3 }} />
			</DialogContent>

			<DialogActions sx={{ p: 3, pt: 2 }}>
				<Button
					onClick={onClose}
					variant="contained"
					fullWidth
					size="medium"
					sx={{
						py: 1.5,
						borderRadius: 2,
						fontSize: '1rem',
						fontWeight: 'medium',
						textTransform: 'none'
					}}
				>
					I Understand. Continue
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DisclaimerModal;
